<script setup lang="ts">
import type { CookieItem } from '~/types/content/cookieItem.type';

const cookies: Record<string, CookieItem[]> = {
    necessary: [
        {
            name: '_GRECAPTCHA',
            provider: 'google.com',
            purpose:
                'Dieses Cookie wird verwendet, um zwischen Menschen und Robotern zu unterscheiden. Dies ist für die Website nützlich, um gültige Berichte über die Nutzung der Website zu erstellen.',
            expiry: '180 Tage',
            type: 'HTTP Cookie',
        },
        {
            name: 'CookieConsent',
            provider: 'consent.cookiebot.com',
            purpose: 'Speichert den Cookie-Zustimmungsstatus des Benutzers für die aktuelle Domain',
            expiry: '1 Jahr',
            type: 'HTTP Cookie',
        },
        {
            name: 'iconify-count',
            provider: 'fleequid.com',
            purpose:
                'Wird vom Content-Management-System (CMS) der Website verwendet, um zu bestimmen, wie die Registerkartenmenüs der Website angezeigt werden sollen.',
            expiry: 'Persistent',
            type: 'HTML Local Storage',
        },
        {
            name: 'iconify-version',
            provider: 'fleequid.com',
            purpose:
                'Wird vom Content-Management-System (CMS) der Website verwendet, um zu bestimmen, wie die Registerkartenmenüs der Website angezeigt werden sollen.',
            expiry: 'Persistent',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::a',
            provider: 'gstatic.com',
            purpose:
                'Dieses Cookie wird verwendet, um zwischen Menschen und Robotern zu unterscheiden. Dies ist für die Website nützlich, um gültige Berichte über die Nutzung der Website zu erstellen.',
            expiry: 'Persistent',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::b',
            provider: 'gstatic.com',
            purpose: 'Dieses Cookie wird verwendet, um zwischen Menschen und Robotern zu unterscheiden.',
            expiry: 'Persistent',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::c',
            provider: 'gstatic.com',
            purpose: 'Dieses Cookie wird verwendet, um zwischen Menschen und Robotern zu unterscheiden.',
            expiry: 'Persistent',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::f',
            provider: 'gstatic.com',
            purpose: 'Dieses Cookie wird verwendet, um zwischen Menschen und Robotern zu unterscheiden.',
            expiry: 'Persistent',
            type: 'HTML Local Storage',
        },
    ],
    preferences: [
        {
            name: 'i18n_redirected',
            provider: 'fleequid.com',
            purpose:
                'Bestimmt die bevorzugte Sprache des Besuchers. Ermöglicht der Website, die bevorzugte Sprache bei der Rückkehr des Besuchers einzustellen.',
            expiry: '1 Jahr',
            type: 'HTTP Cookie',
        },
    ],
    marketing: [
        {
            name: '_ga',
            provider: 'www.googletagmanager.com',
            purpose:
                'Wird verwendet, um Daten an Google Analytics über das Gerät und das Verhalten des Benutzers zu senden. Verfolgt den Benutzer über Geräte und Marketingkanäle hinweg.',
            expiry: '2 Jahre',
            type: 'HTTP Cookie',
        },
        {
            name: '_ga#',
            provider: 'www.googletagmanager.com',
            purpose:
                'Wird verwendet, um Daten an Google Analytics über das Gerät und das Verhalten des Benutzers zu senden. Verfolgt den Benutzer über Geräte und Marketingkanäle hinweg.',
            expiry: '2 Jahre',
            type: 'HTTP Cookie',
        },
    ],
    unclassified: [
        {
            name: 'nuxt-color-mode',
            provider: 'fleequid.com',
            purpose: 'Bestimmt das bevorzugte Theme des Besuchers.',
            expiry: 'Persistent',
            type: 'HTML Local Storage',
        },
    ],
};
</script>
<template>
    <ContentPage title="Cookie-Richtlinie">
        <p>
            Wir verwenden Cookies, um Inhalte und Anzeigen zu personalisieren, Social-Media-Funktionen bereitzustellen und unseren Datenverkehr zu
            analysieren. Wir teilen auch Informationen über Ihre Nutzung unserer Website mit unseren Partnern für Webanalyse, Werbung und soziale
            Medien, die diese möglicherweise mit anderen Informationen kombinieren.
        </p>
        <p>Cookies sind kleine Textdateien, die von Websites verwendet werden können, um die Benutzererfahrung effizienter zu gestalten.</p>
        <p>
            Das Gesetz besagt, dass wir Cookies auf Ihrem Gerät speichern dürfen, wenn sie für den Betrieb dieser Website unbedingt erforderlich sind.
            Für alle anderen Arten von Cookies benötigen wir Ihre Zustimmung.
        </p>
        <p>
            Diese Website verwendet verschiedene Arten von Cookies. Einige Cookies werden von Drittanbieterdiensten gesetzt, die auf unseren Seiten
            erscheinen.
        </p>
        <p>Sie können Ihre Zustimmung jederzeit über die Cookie-Erklärung auf unserer Website ändern oder widerrufen.</p>
        <p>
            Erfahren Sie mehr darüber, wer wir sind, wie Sie uns kontaktieren können und wie wir personenbezogene Daten in unserer
            <NuxtLinkLocale to="privacy-policy" class="text-primary-500 hover:underline">Datenschutzerklärung</NuxtLinkLocale> verarbeiten.
        </p>
        <p>Geben Sie Ihre Zustimmungs-ID und das Datum an, an dem Sie uns bezüglich Ihrer Zustimmung kontaktiert haben.</p>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Notwendig (8)</h3>
            <p>
                Notwendige Cookies helfen dabei, eine Website nutzbar zu machen, indem sie grundlegende Funktionen wie Seitennavigation und Zugang zu
                geschützten Bereichen der Website ermöglichen. Die Website kann ohne diese Cookies nicht richtig funktionieren.
            </p>
            <ContentCookieTable :items="cookies.necessary" />
        </div>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Präferenzen (1)</h3>
            <p>
                Präferenz-Cookies ermöglichen es einer Website, Informationen zu speichern, die das Verhalten oder Aussehen der Website beeinflussen,
                wie z.B. Ihre bevorzugte Sprache oder die Region, in der Sie sich befinden.
            </p>
            <ContentCookieTable :items="cookies.preferences" />
        </div>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Marketing (2)</h3>
            <p>
                Marketing-Cookies werden verwendet, um Besucher über Websites hinweg zu verfolgen. Die Absicht ist, Anzeigen anzuzeigen, die relevant
                und ansprechend für den einzelnen Benutzer sind und daher wertvoller für Verlage und Drittanbieter-Werbetreibende sind.
            </p>
            <ContentCookieTable :items="cookies.marketing" />
        </div>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Nicht klassifiziert (1)</h3>
            <p>Nicht klassifizierte Cookies sind Cookies, die gerade klassifiziert werden, zusammen mit den Anbietern einzelner Cookies.</p>
            <ContentCookieTable :items="cookies.unclassified" />
        </div>
    </ContentPage>
</template>
